import React, { useState, useEffect, useCallback } from "react";
import { handleApiCall } from "../../../apis";
import { endpoints } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { UI_CONSTANTS } from "../../../utils/constants";

const normalizeUrl = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const PrivacyPolicy = ({ role }) => {
  const [privacyUrl, setPrivacyUrl] = useState("");
  const [privacyUrlArabic, setPrivacyUrlArabic] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [id, setId] = useState(null);

  const getCms = useCallback(async () => {
    if (!role) return;

    setFetching(true);
    try {
      const params = new URLSearchParams({
        type: "privacy_policy",
      });

      const response = await handleApiCall("get", `${endpoints.getContent}?${params}`);

      if (response?.data?.code === 200 || response?.data?.code === "200") {
        const item = response?.data?.data?.description_en || response?.data?.data?.description || response?.data?.data?.content || response?.data?.data?.data?.description_en || response?.data?.data?.data?.description || response?.data?.data?.data?.content;
        const itemArabic = response?.data?.data?.description_ar || response?.data?.data?.data?.description_ar || "";
        const fetchedId = response?.data?.data?._id || response?.data?.data?.data?._id;
        setId(fetchedId);
        // Extract URL from description_en (assuming it's stored as plain URL string)
        const url = item || "";
        const arabicUrl = itemArabic || "";
        setPrivacyUrl(url);
        setPrivacyUrlArabic(arabicUrl);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching Privacy Policy:", error);
      toast.error("Failed to fetch Privacy Policy");
    } finally {
      setFetching(false);
    }
  }, [role]);

  useEffect(() => {
    getCms();
  }, [getCms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyUrl.trim()) {
      toast.error("Please enter Privacy Policy URL (English)");
      return;
    }

    if (!privacyUrlArabic.trim()) {
      toast.error("Please enter Privacy Policy URL (Arabic)");
      return;
    }

    const normalizedUrl = normalizeUrl(privacyUrl);
    const normalizedArabicUrl = normalizeUrl(privacyUrlArabic);
    try {
      new URL(normalizedUrl);
    } catch (error) {
      toast.error("Please enter a valid URL");
      return;
    }

    try {
      new URL(normalizedArabicUrl);
    } catch (error) {
      toast.error("Please enter a valid Arabic URL");
      return;
    }

    if (!id) {
      toast.error("Content ID is missing");
      return;
    }

    try {
      setLoading(true);
      const response = await handleApiCall("post", `${endpoints.updateContentManagement}/${id}`, {
        title: "Privacy Policy",
        description_en: normalizedUrl,
        description_ar: normalizedArabicUrl,
      });

      if (response?.data?.code === 201 || response?.data?.code === 200 || response?.data?.code === "200") {
        toast.success("Privacy Policy updated successfully!");
        setPrivacyUrl(normalizedUrl);
        setPrivacyUrlArabic(normalizedArabicUrl);
      } else {
        toast.error(response?.data?.message || "Failed to update Privacy Policy");
      }
    } catch (error) {
      console.error("Error updating Privacy Policy:", error);
      toast.error("Failed to update Privacy Policy");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="card mb-4 px-4 py-4 mt-2" style={{ width: "100%" }}>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4 px-4 py-4 mt-2" style={{ width: "100%" }}>
      <div className="mb-4 px-0 py-2">
        <form onSubmit={handleSubmit}>
          <div className="heading mb-3 d-flex align-items-center">
            <h3 className="text-default weight-400 my-0">Privacy Policy</h3>
            <div className="ml-auto">
              <Button type="submit" className="rounded-lg" disabled={loading}>
                {loading ? UI_CONSTANTS.BUTTON_LABELS.UPDATING || "Updating..." : UI_CONSTANTS.BUTTON_LABELS.UPDATE || "Update"}
              </Button>
            </div>
          </div>
          <div className="row mx-n2">
            <div className="form-group col-md-6 px-2">
              <label className="text-white mb-2">Privacy Policy URL (English)</label>
              <input
                type="url"
                className="form-control default"
                placeholder="Enter Privacy Policy URL (e.g., https://example.com)"
                value={privacyUrl}
                onChange={(e) => setPrivacyUrl(e.target.value)}
              />
            </div>
            <div className="form-group col-md-6 px-2">
              <label className="text-white mb-2">Privacy Policy URL (Arabic)</label>
              <input
                type="url"
                className="form-control default"
                placeholder="أدخل رابط سياسة الخصوصية"
                value={privacyUrlArabic}
                onChange={(e) => setPrivacyUrlArabic(e.target.value)}
                dir="rtl"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
